import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Bell, ChatCircle, File, FolderSimple, Globe, Image, Lock, Sparkle } from '@phosphor-icons/react';
import { LastChanged, RowValue, SettingCard, SettingRow } from '../../../../../../components/settings/setting-card';
import { ValueEditorDialog, type ValueEditor } from '../../../../../../components/settings/value-editor-dialog';
import { formatChangedDate } from '../../../../../../lib/format-edited';
import type { LimitRules } from '@mybusinessmate/schema';
import { Skeleton } from '../../../../../../components/ui/skeleton';
import { useSectionPatch } from '../../../../../../lib/workspace-api';

export const Route = createFileRoute('/subdomain/admin/_staff/settings/workspace/limits')({
  component: LimitsPage,
});

type Field = keyof LimitRules;

const NUMBER: Partial<Record<Field, { title: string; label: string; min: number; max: number; unit: string }>> = {
  auditsPerVisitorPerDay: {
    title: 'Audits per visitor',
    label: 'Per day',
    min: 1,
    max: 50,
    unit: 'Stops one person from running hundreds of audits.',
  },
  auditsPerIpPerDay: {
    title: 'Audits per internet address',
    label: 'Per day',
    min: 1,
    max: 200,
    unit: 'Offices often share one address.',
  },
  taskFileMb: { title: 'Biggest file in a brief or task', label: 'Megabytes', min: 1, max: 2000, unit: 'MB' },
  chatFileMb: { title: 'Biggest file in chat', label: 'Megabytes', min: 1, max: 2000, unit: 'MB' },
  brandKitGb: { title: 'Brand kit storage per business', label: 'Gigabytes', min: 1, max: 100, unit: 'GB' },
  signInTriesPer10Min: {
    title: 'Sign-in tries per internet address',
    label: 'Per 10 minutes',
    min: 3,
    max: 200,
    unit: 'Then we slow them down for 10 minutes.',
  },
};

const TEXT: Partial<Record<Field, { title: string; label: string; options?: string[] }>> = {
  auditPopupWindow: {
    title: 'Show recent audits as pop-ups on the website',
    label: 'How far back',
    options: ['Off', 'Last hour', 'Last 24 hours', 'Last 7 days'],
  },
  allowedFileTypes: {
    title: 'Allowed file types',
    label: 'Allowed types',
    options: ['Images, video, PDF, Office, ZIP', 'Images and PDF only', 'Everything except programs'],
  },
};

function LimitsPage() {
  const { data: limits, patch } = useSectionPatch('limits');
  if (!limits) return <Skeleton className="h-(--mbm-layout-dialog-wide-width) rounded-(--mbm-radius-lg)" />;
  return <LimitsForm limits={limits} patch={patch} />;
}

function LimitsForm({
  limits,
  patch,
}: {
  limits: LimitRules & { changed?: { by: string; at: string } };
  patch: (changes: Partial<LimitRules>) => void;
}) {
  const [editing, setEditing] = useState<Field>();

  let active: { title: string; label: string; editor: ValueEditor } | undefined;
  if (editing) {
    const n = NUMBER[editing];
    const t = TEXT[editing];
    if (n)
      active = {
        title: n.title,
        label: n.label,
        editor: { kind: 'number', value: limits[editing] as number, min: n.min, max: n.max, unit: n.unit },
      };
    else if (t?.options) {
      const value = String(limits[editing]);
      const options = (t.options.includes(value) ? t.options : [value, ...t.options]).map((o) => ({
        value: o,
        label: o,
      }));
      active = { title: t.title, label: t.label, editor: { kind: 'select', value, options } };
    }
  }

  const row = (field: Field, icon: typeof Sparkle, title: string, value: string, subtitle?: string) => (
    <SettingRow
      icon={icon}
      title={title}
      subtitle={subtitle}
      trailing={<RowValue>{value}</RowValue>}
      onClick={() => setEditing(field)}
    />
  );

  return (
    <>
      <SettingCard title="Free business audit" subtitle="Stops one person from running hundreds of audits.">
        {row('auditsPerVisitorPerDay', Sparkle, 'Audits per visitor', `${limits.auditsPerVisitorPerDay} a day`)}
        {row(
          'auditsPerIpPerDay',
          Globe,
          'Audits per internet address',
          `${limits.auditsPerIpPerDay} a day`,
          'Offices often share one address',
        )}
        {row('auditPopupWindow', Bell, 'Show recent audits as pop-ups on the website', limits.auditPopupWindow)}
      </SettingCard>

      <SettingCard title="Files" subtitle="Applies to clients and staff.">
        {row('taskFileMb', File, 'Biggest file in a brief or task', `${limits.taskFileMb} MB`)}
        {row('chatFileMb', ChatCircle, 'Biggest file in chat', `${limits.chatFileMb} MB`)}
        {row(
          'allowedFileTypes',
          Image,
          'Allowed file types',
          limits.allowedFileTypes,
          'Everything else is blocked on upload',
        )}
        {row('brandKitGb', FolderSimple, 'Brand kit storage per business', `${limits.brandKitGb} GB`)}
      </SettingCard>

      <SettingCard title="Sign-in protection">
        {row(
          'signInTriesPer10Min',
          Lock,
          'Sign-in tries per internet address',
          `${limits.signInTriesPer10Min} every 10 min`,
          'Then we slow them down for 10 minutes',
        )}
      </SettingCard>

      <LastChanged by={limits.changed?.by} date={limits.changed ? formatChangedDate(limits.changed.at) : undefined} />

      {/* Sign-in tries per address is enforced by the API now; the other limits are stored for the features that will read them. */}
      {active && editing ? (
        <ValueEditorDialog
          open
          onClose={() => setEditing(undefined)}
          title={active.title}
          label={active.label}
          editor={active.editor}
          onSave={(v) => patch({ [editing]: v } as Partial<LimitRules>)}
        />
      ) : null}
    </>
  );
}
